import React, { useState } from "react";
import { agents } from "@/lib/agentsData";
import { AgentDialog } from "@/components/AgentDialog";
import { IMG } from "@/lib/assets";
import { MousePointerClick, PhoneCall, Handshake, MessageSquareReply, Share2, HeartHandshake, UserSearch, FileAudio, LayoutTemplate, PieChart } from "lucide-react";

const icons = [PhoneCall, Handshake, MessageSquareReply, Share2, HeartHandshake, UserSearch, FileAudio, LayoutTemplate, PieChart];



const AgentIcon = ({ Icon, i }) => (
  <div
    data-testid={`agent-icon-${i}`}
    className="relative w-9 h-9 shrink-0 rounded-[10px] flex items-center justify-center overflow-hidden border border-white/70 shadow-[0_10px_24px_-12px_rgba(40,60,100,0.55)] transition-transform duration-500 group-hover:-translate-y-0.5 group-hover:rotate-[-4deg]"
    style={{ background: "linear-gradient(145deg, #2C3F63 0%, #4F6790 55%, #8EA3C4 100%)" }}
  >
    <span className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.55),transparent_55%)]" />
    <Icon className="relative w-[17px] h-[17px] text-white" strokeWidth={1.6} />
    <span className="absolute top-1.5 right-1.5 w-1 h-1 rounded-full bg-[#E8CE95] shadow-[0_0_6px_rgba(232,206,149,0.9)]" />
  </div>
);

export default function Agents() {
  const [open, setOpen] = useState(null);
  const openIdx = agents.indexOf(open);
  return (
    <section id="agents" className="relative py-14 sm:py-20 bg-[var(--porcelain-deep)] overflow-hidden">
      {/* Matrix / code backdrop — very low opacity, warm-tinted */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none opacity-[0.08]"
        style={{
          backgroundImage: `url(${IMG.matrix})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          filter: "blur(2px) saturate(0.9) sepia(0.35)",
          maskImage:
            "radial-gradient(ellipse 85% 65% at 50% 50%, black 30%, transparent 90%)",
          WebkitMaskImage:
            "radial-gradient(ellipse 85% 65% at 50% 50%, black 30%, transparent 90%)",
        }}
      />
      {/* top dotted */}
      <div className="absolute inset-x-0 top-0 h-4 dotted opacity-50" />

      <div className="relative max-w-[1240px] mx-auto px-5 sm:px-8">
        <div className="mb-8 flex flex-wrap items-center gap-4">
          <span className="num-tile">What We Build</span>
          <span
            data-testid="agents-demo-hint"
            className="inline-flex items-center gap-2 text-[14px] text-[var(--ink-soft)]"
          >
            <MousePointerClick className="w-4 h-4 text-[#A8823A]" strokeWidth={1.8} />
            Click any agent to see how it works and try a live demo.
          </span>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--line)] rounded-2xl overflow-hidden border border-[var(--line)]">
          {agents.map((a, i) => (
            <div
              key={i}
              data-testid={`agent-card-${i}`}
              onClick={() => setOpen(a)}
              className="group cursor-pointer bg-[var(--porcelain-mist)] hover:bg-white px-6 py-5 transition-colors duration-300 relative"
            >
              <div className="flex items-center justify-between gap-3 mb-2">
                <h3 className="font-serif text-[21px] text-[var(--ink)] leading-tight">
                  {a.name}
                </h3>
                <AgentIcon Icon={icons[i]} i={i} />
              </div>
              <p className="text-[14px] leading-[1.6] text-[var(--ink-muted)]">
                {a.desc}
              </p>
              <button
                type="button"
                data-testid={`agent-see-${i}`}
                onClick={(e) => { e.stopPropagation(); setOpen(a); }}
                className="mt-3 inline-flex items-center gap-1.5 text-[13px] font-medium text-[var(--blue)] hover:gap-2.5 transition-[gap]"
              >
                Click to see <span aria-hidden="true">→</span>
              </button>
            </div>
          ))}
        </div>
      </div>
      <AgentDialog agent={open} index={openIdx} onClose={() => setOpen(null)} />
    </section>
  );
}
